import React, { useState, useEffect } from 'react';
import { CloudOff, Upload, Download, Loader2, Check, ExternalLink } from 'lucide-react';
import {
  initGoogleAuth,
  isGoogleConnected,
  disconnectGoogle,
  uploadToGoogleDrive,
  openGooglePicker,
  requestAccessToken,
  downloadFromDrive,
} from '../../lib/googleDrive';

// Google Drive icon with official colors
const GoogleDriveIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA"/>
    <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47"/>
    <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335"/>
    <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.4c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D"/>
    <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.5c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC"/>
    <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.45c0-1.55-.4-3.1-1.2-4.5l-12.7-22z" fill="#FFBA00"/>
  </svg>
);

interface GoogleDriveButtonProps {
  // For saving
  onSave?: () => Promise<{ blob: Blob; fileName: string }>;
  saveLabel?: string;
  // For loading
  onLoad?: (file: File) => void;
  loadLabel?: string;
  // Mode
  mode?: 'save' | 'load' | 'both';
}

export function GoogleDriveButton({
  onSave,
  saveLabel = 'Save to Drive',
  onLoad,
  loadLabel = 'Load from Drive',
  mode = 'save',
}: GoogleDriveButtonProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    initGoogleAuth().then(() => {
      setIsConnected(isGoogleConnected());
    });
  }, []);

  const handleConnect = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await initGoogleAuth();
      await requestAccessToken();
      setIsConnected(true);
    } catch (err: any) {
      setError(err.message || 'Failed to connect to Google Drive');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = () => {
    disconnectGoogle();
    setIsConnected(false);
    setShowDropdown(false);
  };

  const handleSave = async () => {
    if (!onSave) return;
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // First ensure Google auth is initialized
      await initGoogleAuth();
      
      // Then get access token (will prompt for consent if needed)
      await requestAccessToken();
      setIsConnected(true);
      
      // Generate the report
      const { blob, fileName } = await onSave();
      
      // Upload to Drive
      const result = await uploadToGoogleDrive(blob, fileName);
      setSuccess(`Saved!`);
      
      // Open in new tab
      if (result.webViewLink) {
        window.open(result.webViewLink, '_blank');
      }
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Google Drive save error:', err);
      const errorMsg = err.message || 'Failed to save';
      // Show user-friendly error
      if (errorMsg.includes('popup')) {
        setError('Please allow popups for Google sign-in');
      } else if (errorMsg.includes('not loaded')) {
        setError('Loading Google... try again');
      } else {
        setError(errorMsg);
      }
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadClick = async () => {
    if (!onLoad) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Open native Google Drive Picker
      await openGooglePicker(
        async (file) => {
          // User selected a file - download it
          setIsLoading(true);
          try {
            const isGoogleSheet = file.mimeType === 'application/vnd.google-apps.spreadsheet';
            const blob = await downloadFromDrive(file.id, file.mimeType);
            
            // For Google Sheets, change the filename to .csv
            const fileName = isGoogleSheet 
              ? file.name.replace(/$/, '.csv')
              : file.name;
            
            const fileObj = new File([blob], fileName, { 
              type: isGoogleSheet ? 'text/csv' : file.mimeType 
            });
            onLoad(fileObj);
            setSuccess(`Loaded ${fileName}`);
            setTimeout(() => setSuccess(null), 3000);
          } catch (err: any) {
            setError(err.message || 'Failed to download file');
          } finally {
            setIsLoading(false);
          }
        },
        () => {
          // User cancelled
          setIsLoading(false);
        }
      );
      setIsConnected(true);
    } catch (err: any) {
      console.error('Google Drive error:', err);
      const errorMsg = err.message || 'Failed to open picker';
      
      if (errorMsg.includes('popup') || errorMsg.includes('blocked')) {
        setError('Popup blocked! Allow popups for this site and try again.');
      } else if (errorMsg.includes('not loaded')) {
        setError('Loading Google... please try again');
      } else {
        setError(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Simple save button
  if (mode === 'save' && onSave) {
    return (
      <div className="relative">
        <button
          onClick={handleSave}
          disabled={isLoading}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm disabled:opacity-50 ${
            success 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-slate-700/80 hover:bg-slate-600 text-slate-200 border border-slate-600/50 hover:border-slate-500/50'
          }`}
          title={saveLabel}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : success ? (
            <Check className="w-4 h-4" />
          ) : (
            <GoogleDriveIcon className="w-4 h-4" />
          )}
          <span className="hidden sm:inline font-medium">{success || saveLabel}</span>
        </button>
        
        {error && (
          <div className="absolute top-full mt-2 right-0 bg-red-500/10 border border-red-500/20 
                        text-red-400 text-xs p-2 rounded-lg whitespace-nowrap z-50 shadow-lg">
            {error}
          </div>
        )}
      </div>
    );
  }

  // Load button - opens native Google Drive Picker
  if (mode === 'load' && onLoad) {
    return (
      <div className="relative">
        <button
          onClick={handleLoadClick}
          disabled={isLoading}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm disabled:opacity-50 ${
            success 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
          }`}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : success ? (
            <Check className="w-4 h-4" />
          ) : (
            <GoogleDriveIcon className="w-4 h-4" />
          )}
          <span>{success || loadLabel}</span>
        </button>

        {error && (
          <div className="absolute top-full mt-2 left-0 right-0 bg-red-500/10 border border-red-500/20 
                        text-red-400 text-xs p-3 rounded-lg z-50 max-w-xs">
            <p className="font-medium mb-1">Error</p>
            <p>{error}</p>
            <button 
              onClick={() => setError(null)}
              className="mt-2 text-red-300 hover:text-red-200 underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    );
  }

  // Both mode - dropdown with options
  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={isLoading}
        className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 
                 text-slate-200 rounded-lg transition-colors text-sm disabled:opacity-50"
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <GoogleDriveIcon className="w-4 h-4" />
        )}
        <span className="hidden sm:inline">Google Drive</span>
      </button>

      {showDropdown && (
        <div className="absolute top-full mt-2 right-0 bg-slate-800 border border-slate-700 
                      rounded-lg shadow-xl z-50 w-48">
          {onSave && (
            <button
              onClick={handleSave}
              className="w-full flex items-center gap-2 p-3 text-sm text-slate-200 
                       hover:bg-slate-700 transition-colors"
            >
              <Upload className="w-4 h-4" />
              {saveLabel}
            </button>
          )}
          {onLoad && (
            <button
              onClick={handleLoadClick}
              className="w-full flex items-center gap-2 p-3 text-sm text-slate-200 
                       hover:bg-slate-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              {loadLabel}
            </button>
          )}
          <div className="border-t border-slate-700">
            {isConnected ? (
              <button
                onClick={handleDisconnect}
                className="w-full flex items-center gap-2 p-3 text-sm text-red-400 
                         hover:bg-red-500/10 transition-colors"
              >
                <CloudOff className="w-4 h-4" />
                Disconnect
              </button>
            ) : (
              <button
                onClick={handleConnect}
                className="w-full flex items-center gap-2 p-3 text-sm text-blue-400 
                         hover:bg-blue-500/10 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Connect
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="absolute top-full mt-2 right-0 bg-red-500/10 border border-red-500/20 
                      text-red-400 text-xs p-2 rounded-lg whitespace-nowrap z-50">
          {error}
        </div>
      )}
      
      {success && (
        <div className="absolute top-full mt-2 right-0 bg-green-500/10 border border-green-500/20 
                      text-green-400 text-xs p-2 rounded-lg whitespace-nowrap z-50">
          {success}
        </div>
      )}
    </div>
  );
}
